import { GameState } from './GameState';
import { EventBus, GameEvents } from '../core/EventBus';
import { GameConfig } from '../core/GameConfig';
import { Utils } from '../core/Utils';
import { GameData } from '../data/GameData';
import { Contract, PostRecord, ScriptDef, ScriptRoleDef } from '../data/types';
import { noticeSystem } from './NoticeSystem';
import { rankSystem } from './RankSystem';
import { isDramaAired, promoWindowOf } from './ScriptSystem';
import {
    BILLING_DISPLAY_LABELS, DRAMA_PROMO_BIT_POOL, DRAMA_PROMO_MAIN_POOL,
    DRAMA_PROMO_PREMIERE_BIT_POOL, DRAMA_PROMO_PREMIERE_POOL,
} from '../data/tables/WeiboPosts';

/** 发布收益明细（UI 拆报 + likes 推导共用，单一真相） */
export interface PostResult {
    fameGain: number;
    /** 剧宣：蹭剧项/自身项/反哺热度（拆报「蹭剧 +X · 自身 +Y · 热度 +Z」） */
    dramaContribution?: number;
    selfContribution?: number;
    heatGain?: number;
    repGain: number;
    likes: number;
    reposts: number;
}

/**
 * 粉丝系统（剧宣批 2026-09-26 重构；设计=docs/微博剧宣与粉丝改造设计.md）
 * 三分类：营业（日常并入，基础刷存在感）/ 工作=剧宣（限**已开播**剧的合同，见 dramaAired）/ 公益（微量名气+口碑）
 *
 * 定稿规则：
 *  - 一切名气增减按已有名气比例（用户全局原则，⛔ 定额），比例项另设封顶＝下一级台阶÷POST_BIZ_FAME_CAP_DIV
 *    （2026-10-03 名气溢出修：⛔ 顶即复利——满帖玩家年增 ×2.04，第 9 年就吃穿满级线）；
 *  - 免费不扣体力，每月 POST_MONTHLY_LIMIT 条共享上限；剧宣另限同剧每月 1 条；
 *  - fans 落盘：开局=fame×K，月末朝锚点收敛+绕锚扰动（⛔ 读取端现算，刷新不许跳）；
 *  - likes=fans×1%、reposts=fame×1（2026-09-28 重定，⛔ 旧收益正比制），发布瞬间算一次落盘永不再变；
 *  - 近 3 月滚动窗口无发布 → 名气 fame×1% + 粉丝 anchor×5% 双扣（rank≥4 才罚）。
 */
export class FanSystem {
    private static instance: FanSystem;

    private constructor() { }

    static getInstance(): FanSystem {
        if (!this.instance) {
            this.instance = new FanSystem();
        }
        return this.instance;
    }

    /** 计算当前绝对月份（year*12+month，从 0 起，用于滚动窗口比较） */
    private absMonth(): number {
        const p = GameState.getInstance().player;
        return p.year * 12 + p.month;
    }

    /** 本月已发布条数 */
    private countThisMonth(): number {
        const state = GameState.getInstance();
        const m = this.absMonth();
        return state.player.postRecords.filter(r => r.month === m).length;
    }

    // ===== 比例化收益基元 =====

    /**
     * 营业（普通微博）名气收益：比例项 max(1, round(fame×2%)) ∧ 封顶项（升到下一级那道台阶 ÷ POST_BIZ_FAME_CAP_DIV）；
     * 剧宣自身项与此同源同数。封顶是 2026-10-03 名气溢出修的刹车——比例项自带复利
     * （每条读的是被上一条抬高后的名气），⛔ 顶住就会一路指数冲出满级线，详见 GameConfig.POST_BIZ_FAME_CAP_DIV。
     */
    bizFameGain(): number {
        const state = GameState.getInstance();
        const ratio = Math.round(state.getFame() * GameConfig.POST_BIZ_FAME_RATE);
        const cap = Math.round(rankSystem.getIncomeBasis(state.player.rank) / GameConfig.POST_BIZ_FAME_CAP_DIV);
        return Math.max(1, Math.min(ratio, cap));
    }

    /** 剧宣蹭剧权重（⛔ 不复用滴灌 FAME_BILLING_FACTORS，两套独立可调） */
    promoWeight(role: ScriptRoleDef | undefined): number {
        return role?.billing === undefined
            ? GameConfig.PROMO_BILLING_WEIGHTS_NO_BILLING
            : GameConfig.PROMO_BILLING_WEIGHTS[Math.min(role.billing, 5) - 1];
    }

    /** 剧的客观当前热度 = 底数+剧宣累计（甲案封顶×2）+ 客串/好友剧宣累计（**软顶**共池=底数×GUEST_HEAT_CAP_MULT，
     *  B+C 案 2026-10-28：收益随池满衰减⛔锁邀请）；供 UI 读数与公式共用 */
    workHeatOf(script: ScriptDef): number {
        const state = GameState.getInstance();
        // 特殊剧情热度（2026-10-10）：世界事实直接叠加，⛔ 不占剧宣/客串封顶额度
        const plotBonus = state.player.plotHeatBonus?.[script.id] ?? 0;
        const promo = Math.min(script.heat + plotBonus + (state.player.heatPromoted?.[script.id] ?? 0),
            script.heat * GameConfig.PROMO_HEAT_CAP_MULT + plotBonus);
        // 客串与好友剧宣两条通道合计占一个共池（各记各的账、软顶同顶；min 为旧档兜底）
        const guest = Math.min((state.player.guestPromoted?.[script.id] ?? 0)
            + (state.player.friendPromoted?.[script.id] ?? 0),
            script.heat * GameConfig.GUEST_HEAT_CAP_MULT);
        return promo + guest;
    }

    /** 剧宣·剧贡献项 = workHeat × W番位 × k剧（咖位不参与；上限只拦 Δheat ⛔ 不拦本项） */
    promoDramaContribution(script: ScriptDef, role: ScriptRoleDef | undefined): number {
        return Math.round(this.workHeatOf(script) * this.promoWeight(role) * GameConfig.PROMO_DRAMA_K);
    }

    /** 剧宣·反哺 Δheat = fame × Q% × W^(1/3) × min(1, 锚/workHeat)，撞顶归零（Δfame 照常） */
    promoHeatGain(script: ScriptDef, role: ScriptRoleDef | undefined): number {
        const workHeat = this.workHeatOf(script);
        const cap = script.heat * GameConfig.PROMO_HEAT_CAP_MULT;
        if (workHeat >= cap) return 0;
        const raw = GameState.getInstance().getFame() * GameConfig.PROMO_FAME_TO_HEAT_Q
            * Math.cbrt(this.promoWeight(role)) * Math.min(1, GameConfig.PROMO_DECAY_ANCHOR / workHeat);
        return Math.max(0, Math.min(Math.round(raw), Math.round(cap - workHeat)));
    }

    /**
     * 分类系数 × 帖热因子（likes/reposts 同源共用）：帖热因子只给剧宣（公益无剧可热），
     * ⛔ 番位不参与（用户拍板：排面=剧爆+粉丝盘，番位只管实际收益）。
     */
    private postScale(kind: 'biz' | 'promo' | 'charity', workHeat: number): number {
        const coeff = GameConfig.POST_CATEGORY_COEFFS[kind];
        const heatFactor = kind === 'promo' ? 1 + workHeat / GameConfig.PROMO_POST_HEAT_DIV : 1;
        return coeff * heatFactor;
    }

    /** likes 粉丝盘比例制：fans/100 × 分类系数 × 帖热因子 × uniform(1±LIKES_JITTER)，发布瞬间定格 */
    private rollLikes(kind: 'biz' | 'promo' | 'charity', workHeat: number): number {
        const fans = GameState.getInstance().player.fans;
        const jitter = Utils.randomFloat(1 - GameConfig.LIKES_JITTER, 1 + GameConfig.LIKES_JITTER);
        return Math.max(1, Math.round(fans * GameConfig.LIKES_FANS_RATE * this.postScale(kind, workHeat) * jitter));
    }

    /** reposts 与名气同量级（用户口径「转发数量大概和名气差不多」）：fame × 本值 × 同一分类结构 × 独立抖动 */
    private rollReposts(kind: 'biz' | 'promo' | 'charity', workHeat: number): number {
        const fame = GameState.getInstance().getFame();
        const jitter = Utils.randomFloat(1 - GameConfig.REPOSTS_JITTER, 1 + GameConfig.REPOSTS_JITTER);
        return Math.max(0, Math.round(fame * GameConfig.REPOSTS_PER_FAME * this.postScale(kind, workHeat) * jitter));
    }

    // ===== 剧宣文案 =====

    /**
     * 剧宣模板抽取+占位替换：promoCopy 覆盖位优先，其次无名角色用龙套兜底池、有名角色用主池
     * （预览与实际发布须分两次调用=「换一条」语义）。
     * 开播月（剧宣窗口第一月）改用开播月专池（2026-10-24 用户令：开播月剧宣单独开池）。
     */
    rollPromoCopy(script: ScriptDef, role: ScriptRoleDef | undefined, contract?: Contract): string {
        if (script.promoCopy) return script.promoCopy;
        const win = contract ? promoWindowOf(contract, script, this.absMonth()) : null;
        const premiere = !!win && this.absMonth() === win.start;
        const pool = premiere
            ? (role?.name ? DRAMA_PROMO_PREMIERE_POOL : DRAMA_PROMO_PREMIERE_BIT_POOL)
            : (role?.name ? DRAMA_PROMO_MAIN_POOL : DRAMA_PROMO_BIT_POOL);
        const tpl = pool[Math.floor(Math.random() * pool.length)];
        const billingLabel = role?.billing === undefined
            ? BILLING_DISPLAY_LABELS[5] : BILLING_DISPLAY_LABELS[Math.min(role.billing, 5)];
        return tpl
            .replace(/\{script\}/g, script.name)
            .replace(/\{role\}/g, role?.name ?? '')
            .replace(/\{billing\}/g, billingLabel);
    }

    /** 通用池随机一条（避开上一条防连续重复） */
    private rollFromPool(pool: string[], avoid: string): string {
        if (pool.length === 0) return '';
        let next = pool[Math.floor(Math.random() * pool.length)];
        if (next === avoid && pool.length > 1) {
            next = pool[(pool.indexOf(next) + 1) % pool.length];
        }
        return next;
    }

    /** 普通分类（营业/公益）文案预览与发布取文 */
    randomCopy(type: string, avoidText = ''): string {
        const w = GameData.WEIBO_MAP[type];
        return this.rollFromPool(w?.copyPool ?? [], avoidText);
    }

    /** 发布前校验：工作分类必须携 scriptId 且该剧已开播 */
    canPost(type: string, scriptId?: string): boolean {
        const state = GameState.getInstance();
        if (this.countThisMonth() >= GameConfig.POST_MONTHLY_LIMIT) return false;
        if (type === '工作') {
            if (!this.promoContract(scriptId)) return false;
            const m = this.absMonth();
            if (state.player.postRecords.some(r => r.month === m && r.scriptId === scriptId)) return false;
        }
        return true;
    }

    /** 发布（免费）。超月限/剧宣条件不符返回 null。月度窗口与剧宣查重在此收口。 */
    post(type: string, text: string, scriptId?: string): PostResult | null {
        const state = GameState.getInstance();
        const m = this.absMonth();
        if (this.countThisMonth() >= GameConfig.POST_MONTHLY_LIMIT) return null;

        let fameGain = 0, repGain = 0, heatGain = 0;
        let dramaContribution: number | undefined, selfContribution: number | undefined;
        let kind: 'biz' | 'promo' | 'charity' = 'biz';
        let workHeat = 0;

        if (type === '工作') {
            const contract = this.promoContract(scriptId);
            if (!contract) return null;
            const script = GameData.SCRIPT_MAP[contract.scriptId];
            const role = script?.roles[contract.roleIndex];
            if (!script) return null;
            if (state.player.postRecords.some(r => r.month === m && r.scriptId === scriptId)) return null;
            kind = 'promo';
            workHeat = this.workHeatOf(script);
            dramaContribution = this.promoDramaContribution(script, role);
            // 【营销渠道】自营业那半走 `marketingFameGain`（「无效营销」类减益在这一半上打折）；
            // ⛔ 作品分红（dramaContribution）**不走**——那是作品本身给的名气，不算营销。
            selfContribution = state.marketingFameGain(this.bizFameGain());
            heatGain = this.promoHeatGain(script, role);
            fameGain = dramaContribution + selfContribution;
            state.player.heatPromoted = { ...(state.player.heatPromoted ?? {}), [script.id]: (state.player.heatPromoted?.[script.id] ?? 0) + heatGain };
        } else if (type === '公益') {
            kind = 'charity';
            // 比例项 ∧ 台阶封顶（封顶额度=营业的一半）：⛔ 封顶则 Lv.8 起公益反超营业，复利从这条后门回来
            // 【营销渠道】整笔走 `marketingFameGain`
            fameGain = state.marketingFameGain(Math.min(
                Math.round(state.getFame() * GameConfig.CHARITY_FAME_RATE),
                Math.round(rankSystem.getIncomeBasis(state.player.rank) / GameConfig.CHARITY_FAME_CAP_DIV)));
            repGain = GameConfig.CHARITY_REP_GAIN;
            state.addAttribute('reputation', repGain);
        } else {
            // 【营销渠道】普通营业帖：整笔走 `marketingFameGain`
            fameGain = state.marketingFameGain(this.bizFameGain());
        }

        // 数据先于收益结算：这条帖触达的是"发帖前已有的盘子"（fans/fame 都被本帖的收益抬高，⛔ 自反馈放大）
        const likes = this.rollLikes(kind, workHeat);
        const reposts = this.rollReposts(kind, workHeat);

        if (fameGain > 0) state.addFame(fameGain);

        state.player.lastPostMonth = m;
        const record: PostRecord = { month: m, type, text, scriptId, likes, reposts };
        state.player.postRecords.push(record);
        // lifetime 计数（结局批 2026-09-29：结算页「数字人生」数据源；postRecords 是滚动窗口不能当总数）
        state.player.totalPosts = (state.player.totalPosts ?? 0) + 1;
        state.player.totalLikes = (state.player.totalLikes ?? 0) + likes;
        const cutoff = m - GameConfig.POST_RECENT_MONTHS;
        state.player.postRecords = state.player.postRecords.filter(r => r.month >= cutoff);

        if (kind === 'promo') {
        } else if (kind === 'charity') {
        } else {
        }
        return { fameGain, repGain, heatGain, likes, reposts, dramaContribution, selfContribution };
    }

    // ===== 月末结算 =====

    /**
     * 粉丝月末推进（剧宣批，挂 GameLoop 月末管线）：
     * anchor=fame×K 收敛 + 绕锚扰动（按锚点计防漂移）；随机只在此 roll 一次落盘。
     * 钳制 [0, anchor×1.2]：扰动理论可复利越锚，钳制兜住观感（⛔ 粉丝与名气长期脱钩）。
     */
    advanceFans(): void {
        const state = GameState.getInstance();
        const anchor = state.getFanAnchor();
        const recover = (anchor - state.player.fans) * GameConfig.FANS_RECOVER_RATE;
        const jitter = anchor * Utils.randomFloat(-GameConfig.FANS_JITTER_RATE, GameConfig.FANS_JITTER_RATE);
        const next = Math.round(state.player.fans + recover + jitter);
        state.player.fans = Utils.clamp(next, 0, Math.round(anchor * 1.2));
    }

    // checkPostPenalty（连续 N 月未发动态→双扣+小助理通知）已整段删除（2026-10-25 茄子令：
    // 「这条文案通知连带连续几个月没发微博的机制都删掉，脱粉惩罚我会改在别的地方实现」）。
    // ⚠️ 删除范围=惩罚判定+双扣+deroof 通知一并；`postRecords` 滚动窗口维护随方法消失——
    // `getRecentPosts` 自己做了同样的窗口过滤（读侧自足），写侧无人依赖此维护。GameLoop 调用点同步摘除。

    /** 近3个月发布记录（窗口内，按月份倒序；同月内新发布的排上面）——微博页进入/发布后刷新展示用 */
    getRecentPosts(): PostRecord[] {
        const m = this.absMonth();
        const cutoff = m - GameConfig.POST_RECENT_MONTHS;
        // postRecords 按发布时间 push（下标越大越新）：先按下标倒序，再稳定按月份倒序 → 同月内新帖在上
        return GameState.getInstance().player.postRecords
            .map((r, i) => ({ r, i }))
            .filter(x => x.r.month >= cutoff)
            .sort((a, b) => b.r.month - a.r.month || b.i - a.i)
            .map(x => x.r);
    }

    /** 当前粉丝数（v11 落盘字段，⛔ 由 fame 现算——刷新页面粉丝不许自己跳） */
    getFanCount(): number {
        return GameState.getInstance().player.fans;
    }

    /** 本玩家可宣传的合同（⛔ 未开播可宣，见 dramaAired） */
    airingContracts(): Contract[] {
        return GameState.getInstance().player.contracts.filter(c => this.dramaAired(c));
    }

    /**
     * 本月仍可剧宣的剧本 id（去重）：已开播 ∧ 本月未发过该剧宣帖。
     * 底部「微博」tab 的「N部可宣」角标与发布弹窗「工作」签的「可宣」角标共用这一口径，⛔ 两处各算一遍。
     */
    promotableScripts(): string[] {
        const state = GameState.getInstance();
        const m = this.absMonth();
        const promoted = new Set(
            state.player.postRecords.filter(r => r.month === m && r.scriptId).map(r => r.scriptId as string));
        const ids = new Set(this.airingContracts().map(c => c.scriptId));
        for (const id of promoted) ids.delete(id);
        return [...ids];
    }

    /** 剧宣合同查找（同剧多合同取首个已开播的；⛔ 拍摄中/未开播返回 undefined） */
    promoContract(scriptId?: string): Contract | undefined {
        if (!scriptId) return undefined;
        return GameState.getInstance().player.contracts.find(
            c => c.scriptId === scriptId && this.dramaAired(c));
    }

    /**
     * 本月可剧宣？（2026-10-01 收口 + 2026-10-24 改窗口 + 2026-10-25 修实际开播月）
     * ① 剧级档期门控：不能宣「小角色杀青了、全剧还在拍」的剧（判据=isDramaAired，与滴灌/榜单/评选同尺）；
     * ② 窗口 = 开播月 + 播出期 airMonths 个月（见 promoWindowOf）：起点 = 名义门控月与实际开播月的
     *    较晚者（自己戏份拖到门控月后才杀青时，全剧顺延、滴灌也从那时起），长度固定 airMonths + 1。
     * 上界不按 airMonthsLeft 判（那会把剩余发放月数当成可宣月数）；airMonthsLeft 只用来反推开播月。
     */
    private dramaAired(c: Contract): boolean {
        const script = GameData.SCRIPT_MAP[c.scriptId];
        const now = this.absMonth();
        if (!isDramaAired(c, script, now)) return false;
        const win = promoWindowOf(c, script, now);
        if (!win) return true;   // signedMonth 缺省的旧档：沿用旧判据（只看开播门控）
        return now >= win.start && now < win.start + win.months;
    }
}

/** 导出单例 */
export const fanSystem = FanSystem.getInstance();
